import axios from 'axios';
import {callReissueApi} from 'api/LoginApi';
import {tokenStore} from "../store/tokenStore";
import {getAccessTokenSvc, getRefreshTokenSvc, setTokenSvc, clearTokenSvc} from "../common/tokenService";
import {isSystemLevel, isBusinessError} from "../common/errorPolicy";
import {begin as netBegin, end as netEnd, presentRetry as netPresentRetry, isRetryableError as netIsRetryable} from "../util/networkActivity";
import {hasNativeBridge} from "../util/userAgent";

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    timeout: 30000, // 30초 리밋 — 초과 시 전역 오버레이가 '다시 시도' 노출
});

const isLocalDev = window.location.hostname === 'localhost';

function goMaintenance(error) {
    if (isLocalDev) return;
    /*개발용 에러로그 확인 코드 추가*/
    sessionStorage.setItem('lastError', JSON.stringify({
        status: error?.response?.status,
        message: `${error?.message}/${error?.response?.data?.message}`,
        url: error?.config?.url,
        time: new Date().toISOString(),
    }));

    // 점검 페이지에서 또 API 치고 무한 루프 방지
    if (window.location.pathname !== '/maintenance') {
        window.location.href = '/maintenance';
    }
}

apiClient.interceptors.request.use(async (config) => {
    //스토어에 토큰 정보 가져옴
    let accessToken = tokenStore.getAccessToken();
    if (accessToken == null) {
        //토큰 없을시 네이티브에서 꺼내옴
        accessToken = await getAccessTokenSvc();
    }
    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    // 활성유형 헤더 — MuyeonApi 와 동일 규약(서버 활성역할 게이트 판정용)
    try {
        config.headers["X-Active-Type"] = localStorage.getItem("muyeon.activeType") || "GENERAL";
    } catch (e) { /* noop */ }
    netBegin();
    return config;
}, (error) => { netEnd(); return Promise.reject(error); });



/*  토큰 재발급 큐 유틸 */
let isRefreshing = false;
let refreshPromise = null;
let requestQueue = [];

/* 큐에 넣고, 토큰 갱신이 끝나면 cb(token) 실행 결과를 resolve  */
function enqueueRequest(cb) {
    return new Promise((resolve, reject) => {
        requestQueue.push({resolve, reject, cb});
    });
}

/* 토큰 갱신 완료/실패 시 큐 비우기 */
function flushQueue(error, token) {
    requestQueue.forEach(({resolve, reject, cb}) => {
        if (error) {
            reject(error);
        } else {
            try {
                resolve(cb(token));
            } catch (e) {
                reject(e);
            }
        }
    });
    requestQueue = [];
}

/* 안전 파싱 (서버 래핑 레벨이 달라도 동작) */
function extractTokens(res) {
    const payload = res?.data?.data ?? res?.data ?? res;
    return {
        accessToken: payload?.accessToken, refreshToken: payload?.refreshToken,
    };
}


apiClient.interceptors.response.use((response) => { netEnd(); return response; }, async (error) => {
    netEnd();
    const originalRequest = error.config;
    const status = error?.response?.status;
    const errorCode = error?.response?.data?.errorCode;

    // 타임아웃/네트워크 오류(30초 초과 등) → 점검 리다이렉트 대신 전역 오버레이로 '다시 시도'.
    if (netIsRetryable(error) && originalRequest) {
        const action = await netPresentRetry();
        if (action === "retry") return apiClient.request(originalRequest);
        return Promise.reject(error);
    }

    // 401 처리
    if (status === 401) {
        // 같은 요청 무한루프 방지
        if (originalRequest._retry) {
            // 이미 재시도했던 요청이라면 그대로 실패
            return Promise.reject(error);
        }
        originalRequest._retry = true;

        // 대기열에 현재 요청을 등록: 새 토큰 받으면 재시도
        const pending = enqueueRequest((newToken) => {
            // AxiosHeaders 대응해서 Authorization 주입
            if (originalRequest.headers?.set) {
                originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
            } else {
                originalRequest.headers = {
                    ...(originalRequest.headers || {}), Authorization: `Bearer ${newToken}`,
                };
            }
            return apiClient.request(originalRequest);
        });

        // 이미 갱신 중이면, 여기서는 큐에만 넣고 결과만 기다림
        if (isRefreshing && refreshPromise) {
            return pending;
        }

        // 갱신이 없다면 토큰 갱신 시작
        isRefreshing = true;
        refreshPromise = (async () => {
            try {
                let refreshToken = tokenStore.getRefreshToken();
                if (refreshToken == null) refreshToken = await getRefreshTokenSvc();

                const res = await callReissueApi({refreshToken});
                const {accessToken, refreshToken: newRefreshToken} = extractTokens(res);
                if (!accessToken) {
                    // 응답 구조가 다르면 여기 로그로 확인
                    throw new Error('No accessToken in refresh response');
                }

                // 토큰 저장 (객체 인자!) + 기본 헤더 즉시 갱신
                await setTokenSvc({accessToken, refreshToken: newRefreshToken});
                apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

                // 큐 처리 (성공)
                flushQueue(null, accessToken);
            } catch (err) {
                // 큐 처리 (실패) + 로그아웃
                flushQueue(err, null);
                try {
                    await clearTokenSvc();
                } catch {
                }
                // 토큰 갱신 실패(만료·비로그인) 처리:
                //  · 네이티브 앱: 기존대로 로그인 화면으로.
                //  · 웹(브라우저): 강제 이동하지 않고 '로그아웃 상태'로만 전환한다.
                //    → 공개 페이지(홈·목록·상세)는 그대로 열람 가능(광고 유입자), 인증 필요
                //      라우트만 PrivateRouter 가 로그인으로 보낸다. (만료 세션이 남은 브라우저가
                //      홈에서 로그인으로 튕기던 문제 해결)
                if (hasNativeBridge()) {
                    window.location.replace('/login');
                } else {
                    try { localStorage.removeItem('muyeonAuth'); } catch {}
                    try { window.dispatchEvent(new Event('muyeonForceLogout')); } catch {}
                }
            } finally {
                isRefreshing = false;
                refreshPromise = null;
            }
        })();

        // 현재 요청은 큐에 넣었으니 그 결과만 기다림
        return pending;
    } else if (status === 409) {
        return Promise.reject({
            type: 'DUPLICATE', data: error.response?.data || '이미 존재하는 데이터예요.',
        });
    } else if (status === 413) {
        return Promise.reject({
            type: 'EXCEED_MAX_FILE_SIZE', data: error.response?.data, message: "업로드할 수 있는 용량을 넘었어요."
        });
    }

    // 비즈니스 에러 → 화면단 처리
    if (isBusinessError(errorCode)) {
        return Promise.resolve(error.response);
    }

    // 시스템 레벨 → 점검 페이지
    if (isSystemLevel(status)) {
        goMaintenance(error);
        return Promise.reject(error);
    }

    // 그 외 → 기본 점검
    goMaintenance(error);
    return Promise.reject(error);
});

export default apiClient;
